import { useState, useEffect } from 'react';
import { replaceBucketKeysWithUrls } from '@shootsta/client-aws-s3';

/**
 * Signs a raw S3 bucket key into a pre-signed URL.
 *
 * @param {object|null} apolloClient - Apollo client instance (from ApolloConsumer)
 * @param {string} rawImageUrl      - Raw S3 bucket key (may be empty/null)
 * @param {string} organisationId   - Organisation ID required for signing
 * @returns {string} Signed URL, or the raw value (or '') as a fallback
 */
export default function useSignedImageUrl(apolloClient, rawImageUrl, organisationId) {
  const [signedUrl, setSignedUrl] = useState(rawImageUrl || '');

  useEffect(() => {
    let cancelled = false;

    if (!rawImageUrl || !organisationId || !apolloClient) {
      setSignedUrl(rawImageUrl || '');
      return undefined;
    }

    (async () => {
      try {
        const { imageUrl: preSignedImageUrl } = await replaceBucketKeysWithUrls({
          apiClient: apolloClient,
          data: { imageUrl: rawImageUrl },
          substitutedKeys: ['imageUrl'],
          organisationId
        });

        if (!cancelled) {
          setSignedUrl(preSignedImageUrl || rawImageUrl);
        }
      } catch {
        if (!cancelled) {
          // Fall back to raw value so placeholderText still works
          setSignedUrl(rawImageUrl);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, rawImageUrl, organisationId]);

  return signedUrl;
}
